import { na1 } from 'hubspot-url-utils/hublets';
import Raven from 'raven-js';
import { ACADEMY_PIP_SESSION_KEY } from 'navigation-components/config';
import enviro from 'enviro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectEvent } from '../../hooks/useEffectEvent';
import HubSpotVideoPlayer from 'ui-addon-video-player/components/HubSpotVideoPlayer';
import throttle from 'hs-lodash/throttle';
import { convertToMinutes } from '../../utils/time';
import { useAcademyVideoHost } from '../../hooks/useAcademyVideoHost';
import { jsx as _jsx } from "react/jsx-runtime";
const ON_TIME_PROGRESS_THROTTLE_INTERVAL = 3000;
const ACVideoPlayer = ({
  videoId,
  autoPlay,
  startTime,
  mutedByDefault,
  onError = () => {},
  onFinish = () => {},
  onLoaded = () => {},
  onLoading = () => {},
  onPause = () => {},
  onPlay = () => {},
  onReady = () => {},
  onProgress = () => {},
  onTimeUpdate: onTimeUpdateProp = () => {},
  onPlayBackRateChange = () => {},
  onTimeProgress = () => {},
  onMilestone = () => {},
  onAbandon = () => {},
  onActivation = () => {},
  playbackRate = 1,
  creatorHubId,
  creatorRegion = na1,
  duration,
  activationThreshold = 10,
  subtitleLanguage,
  enablePictureInPicture = true,
  playerType,
  onToggleAutoPlay = () => {},
  enableAutoPlayToggle = false
}) => {
  // Remount key to force player refresh when needed
  const [remountKey, setRemountKey] = useState(0);
  const {
    showAcademyVideoHost,
    hideAcademyVideoHost
  } = useAcademyVideoHost();
  const isPictureInPictureActiveRef = useRef(false);
  const currentTimeRef = useRef(undefined);
  const previousTime = useRef(startTime || 0);
  const throttledTimeUpdateMinutesWatched = useMemo(() => throttle(seconds => {
    const timeDifference = seconds - previousTime.current;
    previousTime.current = seconds;
    if (timeDifference > 0 &&
    // To exclude view duration events triggered by user-initiated time scrubbing via the video progress bar
    timeDifference < ON_TIME_PROGRESS_THROTTLE_INTERVAL * 2) {
      const viewDuration = convertToMinutes(timeDifference);
      onTimeProgress(viewDuration);
    }
  }, ON_TIME_PROGRESS_THROTTLE_INTERVAL, {
    leading: false,
    trailing: true
  }), [onTimeProgress]);
  const activationEventFired = useRef(false);
  const milestone25Fired = useRef(false);
  const milestone50Fired = useRef(false);
  const milestone75Fired = useRef(false);
  const hasFinished = useRef(false);
  const timeWatched = useRef(0);
  const granularPreviousTime = useRef(startTime || 0);
  const fireAbandon = useEffectEvent(() => {
    if (timeWatched.current > 0 && !hasFinished.current && duration !== undefined && duration > 0) {
      onAbandon(timeWatched.current, duration, granularPreviousTime.current);
    }
  });
  useEffect(() => {
    return () => {
      fireAbandon();
    };
  }, []);
  const handleTimeUpdate = useCallback(seconds => {
    onTimeUpdateProp(seconds);
    const timeDelta = seconds - granularPreviousTime.current;
    if (timeDelta > 0 &&
    // prevents updating if the user skips backwards
    timeDelta < 1 // prevents updating if the users skips too far forwards
    ) {
      timeWatched.current += seconds - granularPreviousTime.current;
    }
    granularPreviousTime.current = seconds;
    throttledTimeUpdateMinutesWatched(seconds);
    if (timeWatched.current > activationThreshold && !activationEventFired.current) {
      onActivation();
      activationEventFired.current = true;
    }
    if (duration && duration > 0) {
      const ratio = timeWatched.current / duration;
      if (!milestone25Fired.current && ratio >= 0.25) {
        milestone25Fired.current = true;
        onMilestone(25);
      }
      if (!milestone50Fired.current && ratio >= 0.5) {
        milestone50Fired.current = true;
        onMilestone(50);
      }
      if (!milestone75Fired.current && ratio >= 0.75) {
        milestone75Fired.current = true;
        onMilestone(75);
      }
    }
  }, [onTimeUpdateProp, throttledTimeUpdateMinutesWatched, activationThreshold, onActivation, onMilestone, duration]);
  const handlePlay = useCallback(() => {
    onPlay();
    try {
      const pipSessionValue = sessionStorage.getItem(ACADEMY_PIP_SESSION_KEY);
      if (playerType !== 'academyVideoHost' && playerType !== 'compactPlayerWithCustomMenu' && pipSessionValue) {
        setRemountKey(prev => prev === 0 ? 1 : prev);
        void hideAcademyVideoHost();
      }
    } catch (error) {
      Raven.captureException(error);
    }
  }, [onPlay, playerType, hideAcademyVideoHost]);
  const handleProgressHsVideo = useCallback(e => {
    if (e && e.detail) {
      currentTimeRef.current = e.detail.currentTime;
      onProgress({
        currentTime: e.detail.currentTime,
        subtitleLanguage: e.detail.textLanguage
      });
    }
  }, [onProgress]);
  const handleTimeUpdateHsVideo = useCallback(e => {
    if (e && e.detail && e.detail.currentTime) {
      handleTimeUpdate(e.detail.currentTime);
    }
  }, [handleTimeUpdate]);
  const handleFinish = useCallback(() => {
    hasFinished.current = true;
    onFinish();
    if (!activationEventFired.current) {
      onActivation();
      activationEventFired.current = true;
    }
  }, [onActivation, onFinish]);
  const handleLoaded = useCallback(() => {
    onLoaded();
  }, [onLoaded]);
  const handleLoading = useCallback(() => {
    onLoading();
  }, [onLoading]);
  const handleTogglePictureInPicture = useCallback(e => {
    isPictureInPictureActiveRef.current = e.detail.isPictureInPictureActive;
    currentTimeRef.current = e.detail.currentTime;
  }, []);
  const handleRegisterAcademyVideoMessage = useCallback(() => {
    try {
      const pipSessionValue = sessionStorage.getItem(ACADEMY_PIP_SESSION_KEY);
      if (isPictureInPictureActiveRef.current && !pipSessionValue) {
        sessionStorage.setItem(ACADEMY_PIP_SESSION_KEY, JSON.stringify({
          videoId,
          startTime: currentTimeRef.current,
          creatorHubId: creatorHubId !== null && creatorHubId !== void 0 ? creatorHubId : undefined,
          creatorRegion: creatorRegion !== null && creatorRegion !== void 0 ? creatorRegion : undefined
        }));
        void showAcademyVideoHost();
      }
    } catch (error) {
      Raven.captureException(error);
    }
  }, [videoId, creatorHubId, creatorRegion, showAcademyVideoHost]);
  useEffect(() => {
    window.addEventListener('pagehide', handleRegisterAcademyVideoMessage);
    return () => {
      window.removeEventListener('pagehide', handleRegisterAcademyVideoMessage);
      void handleRegisterAcademyVideoMessage();
    };
  }, [handleRegisterAcademyVideoMessage]);
  return /*#__PURE__*/_jsx("div", {
    "data-player-type": "HUBSPOT_VIDEO",
    "data-player-video-id": videoId,
    "data-test-id": "ac-video-player",
    children: /*#__PURE__*/_jsx(HubSpotVideoPlayer, {
      autoPlay: remountKey > 0 ? true : autoPlay,
      currentTime: startTime !== undefined && startTime >= 0 ? startTime : undefined,
      enableAutoPlayToggle: enableAutoPlayToggle,
      enableParentHotKeys: true,
      enablePictureInPicture: enablePictureInPicture,
      enableVideoFrameHotKeys: true,
      env: enviro.isQa() ? 'qa' : 'prod',
      mutedByDefault: mutedByDefault,
      onError: onError,
      onFinish: handleFinish,
      onLoaded: handleLoaded,
      onLoading: handleLoading,
      onPause: onPause,
      onPlay: handlePlay,
      onPlayBackRateChange: onPlayBackRateChange,
      onReady: onReady,
      onTimeUpdate: handleTimeUpdateHsVideo,
      onToggleAutoPlay: onToggleAutoPlay,
      onTogglePictureInPicture: handleTogglePictureInPicture,
      onUpdateProgress: handleProgressHsVideo,
      playbackRate: playbackRate,
      playerType: playerType,
      portalId: creatorHubId,
      preload: "auto",
      region: creatorRegion,
      subtitleLanguage: subtitleLanguage,
      videoId: videoId,
      videoPlaySpeed: playbackRate * 100
    }, `${videoId}-${remountKey}`)
  });
};
export default ACVideoPlayer;